import { User } from 'oidc-client-ts';
import { userManager } from '@src/auth/oidcConfig';

// Remaining access-token lifetime below which we renew before sending a
// request. Keycloak issues 30 minute tokens, so this only trips near the end of
// a token's life or after the device was suspended.
export const REFRESH_THRESHOLD_SECONDS = 60;

let inFlight: Promise<User | null> | null = null;

/**
 * Renews the session, collapsing concurrent callers onto a single request.
 *
 * A page load fires several queries at once, so without this guard each one
 * would call signinSilent() with the same refresh token. Keycloak currently has
 * "Revoke Refresh Token" disabled so the parallel calls happen to succeed, but
 * they are redundant, and all but one would fail the moment rotation is enabled.
 */
export function renewSession(): Promise<User | null> {
  if (inFlight) {
    return inFlight;
  }
  const request = userManager
    .signinSilent()
    .catch((error: unknown) => {
      // Caller decides what to do with a dead session; queryClient logs out
      // once a request still comes back 401 after this returned null.
      console.warn('OIDC: silent renew failed', error);
      return null;
    })
    .finally(() => {
      if (inFlight === request) {
        inFlight = null;
      }
    });
  inFlight = request;
  return request;
}

/**
 * Resolves the current session, renewing it when the stored access token is
 * expired or about to expire. Returns null when there is nothing to recover.
 *
 * The freshness check happens per call rather than relying on
 * automaticSilentRenew's accessTokenExpiring timer. That timer is a setInterval
 * and does not fire on schedule when a tab is backgrounded or the device is
 * suspended, which is why mobile clients saw a far higher 401 rate than desktop.
 */
export async function getActiveUser(): Promise<User | null> {
  const user = await userManager.getUser();
  const expiresIn = user?.expires_in ?? 0;
  if (user?.access_token && expiresIn > REFRESH_THRESHOLD_SECONDS) {
    return user;
  }
  // Only renew when there is a refresh token to renew with. Without one,
  // signinSilent() falls back to a hidden iframe and blocks for its whole
  // timeout before failing, which would stall every call made while logged out.
  if (!user?.refresh_token) {
    return null;
  }
  return renewSession();
}

/** Returns an access token that is safe to send, or undefined if there is none. */
export async function getValidAccessToken(): Promise<string | undefined> {
  const user = await getActiveUser();
  return user?.access_token;
}

/** Test seam: drops any in-flight renewal so cases cannot leak into each other. */
export function resetRenewStateForTests(): void {
  inFlight = null;
}
